import { useState } from "react";
import { Mail, MapPin, Send, Youtube, Facebook, Instagram, Twitter, Globe, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const socialLinks = [
  { 
    name: "YouTube", 
    icon: Youtube, 
    href: "https://youtube.com/@TheCarbonMeridian", 
    color: "bg-red-600 hover:bg-red-700 text-white",
    handle: "@TheCarbonMeridian"
  },
  { 
    name: "Facebook", 
    icon: Facebook, 
    href: "https://www.facebook.com/profile.php?id=61584859585945", 
    color: "bg-blue-600 hover:bg-blue-700 text-white",
    handle: "The Carbon Meridian"
  },
  { 
    name: "Instagram", 
    icon: Instagram, 
    href: "https://www.instagram.com/thecarbonmeridian/", 
    color: "bg-gradient-to-br from-purple-600 via-pink-500 to-orange-400 text-white",
    handle: "thecarbonmeridian"
  },
  { 
    name: "X (Twitter)", 
    icon: Twitter, 
    href: "https://x.com/carbonmeridian", 
    color: "bg-slate-900 hover:bg-black text-white",
    handle: "@carbonmeridian"
  },
];

export default function ContactPage() {
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: ""
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      await axios.post(`${API}/contact`, formData);
      toast.success("Message sent successfully! We'll get back to you soon.");
      setFormData({ name: "", email: "", subject: "", message: "" });
    } catch (error) {
      console.error("Contact form error:", error);
      toast.error("Failed to send message. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen pt-20 bg-white" data-testid="contact-page">
      {/* Hero */}
      <section className="py-16 lg:py-20 bg-gradient-to-br from-forest-50 to-earth-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="font-montserrat text-4xl sm:text-5xl font-bold text-slate-900 mb-4">
            Contact Us
          </h1>
          <p className="text-xl text-slate-600 max-w-2xl mx-auto">
            Have questions about carbon markets, our services, or want to collaborate? 
            We'd love to hear from you.
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-3 gap-12">
            {/* Contact Info */}
            <div className="space-y-8">
              <div>
                <h2 className="font-montserrat text-2xl font-bold text-slate-900 mb-6">
                  Get in Touch
                </h2>
                <p className="text-slate-600 leading-relaxed">
                  Whether you're an exporter needing CBAM support, a business looking to offset emissions, 
                  or an individual interested in our programs, our team is here to help.
                </p>
              </div>

              {/* Email Card */}
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="p-3 bg-forest-100 rounded-xl">
                      <Mail className="h-6 w-6 text-forest-700" />
                    </div>
                    <div>
                      <p className="font-semibold text-slate-900 mb-1">Email</p>
                      <a href="mailto:netzero@thecarbonmeridian.com" className="text-forest-700 hover:underline font-medium">
                        netzero@thecarbonmeridian.com
                      </a>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Website Card */}
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="p-3 bg-earth-100 rounded-xl">
                      <Globe className="h-6 w-6 text-earth-700" />
                    </div>
                    <div>
                      <p className="font-semibold text-slate-900 mb-1">Website</p>
                      <a 
                        href="https://www.thecarbonmeridian.com" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-earth-700 hover:underline font-medium flex items-center gap-1"
                      >
                        www.thecarbonmeridian.com
                        <ExternalLink className="h-3 w-3" />
                      </a>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Address Card */}
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="p-3 bg-teal-100 rounded-xl">
                      <MapPin className="h-6 w-6 text-teal-700" />
                    </div>
                    <div>
                      <p className="font-semibold text-slate-900 mb-1">Address</p>
                      <p className="text-slate-600">Chandigarh, India</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Social Media Section */}
              <div>
                <h3 className="font-montserrat font-semibold text-slate-900 mb-4">Follow Us</h3>
                <div className="grid grid-cols-2 gap-3">
                  {socialLinks.map((social) => (
                    <a
                      key={social.name}
                      href={social.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`flex items-center gap-3 p-4 rounded-xl ${social.color} transition-transform hover:scale-105 shadow-md`}
                      data-testid={`contact-social-${social.name.toLowerCase().replace(/[^a-z]/g, '')}`}
                    >
                      <social.icon className="h-6 w-6 flex-shrink-0" />
                      <div className="min-w-0">
                        <p className="text-xs opacity-80">{social.name}</p>
                        <p className="text-sm font-medium truncate">{social.handle}</p>
                      </div>
                    </a>
                  ))}
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-2">
              <Card>
                <CardContent className="p-6 lg:p-8">
                  <h3 className="font-montserrat text-xl font-bold text-slate-900 mb-6">
                    Send us a Message
                  </h3>
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="name">Full Name *</Label>
                        <Input
                          id="name"
                          required
                          value={formData.name}
                          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                          placeholder="Your name"
                          data-testid="contact-name"
                        />
                      </div>
                      <div>
                        <Label htmlFor="email">Email *</Label>
                        <Input
                          id="email"
                          type="email"
                          required
                          value={formData.email}
                          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                          placeholder="your@email.com"
                          data-testid="contact-email"
                        />
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="subject">Subject *</Label>
                      <Input
                        id="subject"
                        required
                        value={formData.subject}
                        onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                        placeholder="How can we help?"
                        data-testid="contact-subject"
                      />
                    </div>
                    <div>
                      <Label htmlFor="message">Message *</Label>
                      <Textarea
                        id="message"
                        required
                        rows={6}
                        value={formData.message}
                        onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                        placeholder="Tell us more about your inquiry..."
                        data-testid="contact-message"
                      />
                    </div>
                    <Button
                      type="submit"
                      className="w-full sm:w-auto bg-forest-950 hover:bg-forest-900"
                      disabled={submitting}
                      data-testid="contact-submit"
                    >
                      {submitting ? "Sending..." : "Send Message"}
                      <Send className="ml-2 h-4 w-4" />
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
